import { MicrositeTemplate } from '~/model';
import { useRegisterSection } from '~/hooks/useSections';
import { type ComponentConfig } from '@puckeditor/core';
import WysiwygBlockAlpha from '~/editor/templates/alpha/WysiwygBlockAlpha';
import WysiwygBlockDelta from '~/editor/templates/delta/WysiwygBlockDelta';
import WysiwygBlockEpsilon from '~/editor/templates/epsilon/WysiwygBlockEpsilon';
import WysiwygBlockEta from '~/editor/templates/eta/WysiwygBlockEta';
import WysiwygBlockZeta from '~/editor/templates/zeta/WysiwygBlockZeta';
import { DEMO_LISTING_DATA } from '~/demo';
import WysiwygBlockBeta from '~/editor/templates/beta/WysiwygBlockBeta';
import type { WysiwygBlockVariant } from '~/editor/blocks/wysiwyg/common';

export interface WysiwygBlockProps {
	navigation?: boolean;
	title: string;
	content: string;
	template: MicrositeTemplate;
}

export const PUCK_BLOCK_WYSIWYG: ComponentConfig<WysiwygBlockProps> = {
	label: 'WYSIWYG Editor',
	fields: {
		navigation: {
			type: 'radio',
			options: [
				{ label: 'On', value: true },
				{ label: 'Off', value: false },
			],
		},
		title: {
			type: 'text',
		},
		content: {
			type: 'text',
		},
		template: {
			type: 'radio',
			options: [
				{ label: 'Alpha', value: MicrositeTemplate.ALPHA },
				{ label: 'Beta', value: MicrositeTemplate.BETA },
				{ label: 'Delta', value: MicrositeTemplate.DELTA },
				{ label: 'Epsilon', value: MicrositeTemplate.EPSILON },
				{ label: 'Eta', value: MicrositeTemplate.ETA },
				{ label: 'Zeta', value: MicrositeTemplate.ZETA },
			],
		},
	},
	defaultProps: {
		navigation: true,
		title: 'Enter Title Here',
		content: 'Enter Content Here',
		template: MicrositeTemplate.ETA,
	},
	permissions: {
		duplicate: false,
	},
	render(props) {
		return <WysiwygBlock {...props} />;
	},
};

function getParagraphs(description: string) {
	return description
		.split(/\n\s*\n/)
		.map((paragraph) => paragraph.trim())
		.filter(Boolean);
}

export default function WysiwygBlock({ navigation = true, title, template, content }: WysiwygBlockProps) {
	const id = useRegisterSection('wysiwyg', title, navigation);

	let text = DEMO_LISTING_DATA.body ?? content;

	let html = <div dangerouslySetInnerHTML={{ __html: text }} />;

	if (!text.includes('/>')) {
		html = (
			<>
				{getParagraphs(text).map((p) => (
					<p>{p}</p>
				))}
			</>
		);
	}

	const props: WysiwygBlockVariant = {
		id,
		children: html,
		title,
	};

	let block = <></>;

	if (template === MicrositeTemplate.ALPHA) {
		block = <WysiwygBlockAlpha description={text} />;
	}
	if (template === MicrositeTemplate.BETA) {
		return <WysiwygBlockBeta {...props} />;
	}
	if (template === MicrositeTemplate.DELTA) {
		block = <WysiwygBlockDelta description={text} />;
	}
	if (template === MicrositeTemplate.EPSILON) {
		block = <WysiwygBlockEpsilon description={text} />;
	}
	if (template === MicrositeTemplate.ETA) {
		block = <WysiwygBlockEta description={text} />;
	}
	if (template === MicrositeTemplate.ZETA) {
		block = <WysiwygBlockZeta description={text} />;
	}

	return <div id={id}>{block}</div>;
}
